import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'

const API_URL = import.meta.env.VITE_API_URL || ''

interface Business {
  id: string
  name: string
  status: string
  timezone: string
  whatsappNumber?: string
}

interface Service {
  id: string
  name: string
  description: string | null
  durationMinutes: number
  priceCents: number
  active: boolean
}

export const Route = createFileRoute('/b/$businessId')({
  component: BusinessProfilePage,
})

function BusinessProfilePage() {
  const { businessId } = Route.useParams()
  const [business, setBusiness] = useState<Business | null>(null)
  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      try {
        const [bizRes, svcRes] = await Promise.all([
          fetch(`${API_URL}/api/v1/businesses/${businessId}`),
          fetch(`${API_URL}/api/v1/services/business/${businessId}`),
        ])

        if (!bizRes.ok) {
          setError('Business not found')
          return
        }

        const bizData = await bizRes.json()
        setBusiness(bizData.data ?? bizData)

        if (svcRes.ok) {
          const svcData = await svcRes.json()
          const items = svcData.data ?? svcData
          setServices(Array.isArray(items) ? items.filter((s: Service) => s.active) : [])
        }
      } catch {
        setError('Failed to load business profile')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [businessId])

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <p>Loading...</p>
      </div>
    )
  }

  if (error || !business) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600 }}>Business not found</h1>
          <p style={{ color: '#666', marginTop: '0.5rem' }}>This business profile does not exist.</p>
        </div>
      </div>
    )
  }

  const whatsappPhone = (business.whatsappNumber || '').replace(/[^0-9]/g, '')
  const whatsappUrl = `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(`Oi! Gostaria de agendar um horario no ${business.name}`)}`

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '2rem 1rem' }}>
      <header style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <div
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#25D366',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
            fontSize: '2rem',
            color: 'white',
            fontWeight: 700,
          }}
        >
          {business.name.charAt(0).toUpperCase()}
        </div>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, margin: 0 }}>{business.name}</h1>
        <p style={{ color: '#666', marginTop: '0.25rem' }}>{business.timezone.replace('_', ' ')}</p>
      </header>

      {services.length > 0 && (
        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Services</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {services.map((svc) => (
              <div
                key={svc.id}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '1rem',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <h3 style={{ fontWeight: 600, margin: 0 }}>{svc.name}</h3>
                  <span style={{ fontWeight: 600, color: '#25D366' }}>
                    R$ {(svc.priceCents / 100).toFixed(2)}
                  </span>
                </div>
                {svc.description && (
                  <p style={{ color: '#666', fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
                    {svc.description}
                  </p>
                )}
                <p style={{ color: '#999', fontSize: '0.75rem', margin: '0.25rem 0 0' }}>
                  {svc.durationMinutes} min
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'block',
          width: '100%',
          padding: '1rem',
          backgroundColor: '#25D366',
          color: 'white',
          textAlign: 'center',
          borderRadius: '8px',
          fontSize: '1.125rem',
          fontWeight: 600,
          textDecoration: 'none',
        }}
      >
        Book via WhatsApp
      </a>
    </div>
  )
}
